import React, { useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../../services/api';

// Superadmin plan, price and add-on catalogue. Payment-provider credentials
// have their own page because they are Key Vault references, not billing data.
//
// Everything here is priced in public and applies to live subscribers the
// moment it is saved, so the page is built around three rules:
//
//   1. One tier on screen at a time. Capacity and price for the SAME plan sit
//      together, because they are one commercial decision -- a nine-column
//      matrix of bare number boxes invites editing the wrong row.
//   2. Nothing is written until it has been read back. Every save goes through
//      a review panel listing old -> new for each field, and anything that
//      cuts capacity or moves a price by half has to be acknowledged.
//   3. A draft is never mistaken for the truth. Changed fields are marked and
//      show the stored value; a successful save clears its own draft so the
//      form falls back to what the server actually has.
const TIERS = ['starter', 'growth', 'scale'];
const CURRENCIES = ['INR', 'USD'];

const TABS = [
  { id: 'plans', label: 'Plans & prices' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'payments', label: 'Payments' },
  { id: 'tax', label: 'Tax' },
];

// A price move this large in either direction is almost always a typed or
// missing zero, so it is called out rather than merely allowed.
const LARGE_MOVE_PERCENT = 50;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const tenantIdLooksValid = (value) => UUID_PATTERN.test(String(value || '').trim());

// Capacity fields grouped for editing. The server owns the field list; this
// only decides which box a known key sits in. Anything the server adds later
// falls through to "Other capacity" rather than vanishing -- a limit editable
// in the API but invisible here is how a plan silently keeps a stale value.
const LIMIT_GROUPS = [
  { title: 'Conversation volume', keys: ['max_ai_answers', 'max_ai_answers_inr'] },
  { title: 'Knowledge', keys: ['max_websites', 'max_knowledge_pages'] },
  { title: 'Workspace', keys: ['max_agents', 'max_users'] },
  {
    title: 'Included alert contacts',
    keys: ['included_notification_phones', 'included_notification_emails'],
  },
  { title: 'Trial', keys: ['trial_days'] },
];

export const groupLimitFields = (fields) => {
  const byKey = new Map((fields || []).map((field) => [field.key, field]));
  const groups = LIMIT_GROUPS
    .map((group) => ({
      title: group.title,
      fields: group.keys.map((key) => byKey.get(key)).filter(Boolean),
    }))
    .filter((group) => group.fields.length > 0);
  const placed = new Set(LIMIT_GROUPS.flatMap((group) => group.keys));
  const rest = (fields || []).filter((field) => !placed.has(field.key));
  return rest.length ? [...groups, { title: 'Other capacity', fields: rest }] : groups;
};

const errorText = (e) => e?.response?.data?.detail || e?.message || 'Unknown error';

const Field = ({ label, was, hint, children }) => (
  <label className="block">
    <span className="flex items-baseline justify-between gap-2">
      <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
      {was != null && (
        <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
          was {was}
        </span>
      )}
    </span>
    {children}
    {hint && (
      <span className="mt-0.5 block text-[11px] text-gray-400 dark:text-gray-500">{hint}</span>
    )}
  </label>
);

const inputClass = (changed) => [
  'mt-1 w-full rounded border px-2 py-1 text-sm',
  'bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100',
  changed
    ? 'border-amber-400 ring-1 ring-amber-300 dark:border-amber-500 dark:ring-amber-600'
    : 'border-gray-300 dark:border-gray-600',
].join(' ');

const Card = ({ children }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
    {children}
  </div>
);

const SaveBar = ({ changes, onReview, onDiscard }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
    <span className={`text-sm ${changes.length
      ? 'text-amber-700 dark:text-amber-400 font-medium'
      : 'text-gray-400 dark:text-gray-500'}`}>
      {changes.length === 0
        ? 'No unsaved changes'
        : `${changes.length} unsaved change${changes.length === 1 ? '' : 's'}`}
    </span>
    <div className="flex items-center gap-2">
      <button
        type="button" onClick={onDiscard} disabled={changes.length === 0}
        className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-40"
      >
        Discard
      </button>
      <button
        type="button" onClick={onReview} disabled={changes.length === 0}
        className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
      >
        Review changes
      </button>
    </div>
  </div>
);

const BillingConfig = () => {
  const [planCatalog, setPlanCatalog] = useState({ plans: [], limit_fields: [] });
  const [prices, setPrices] = useState([]);
  const [addonCatalog, setAddonCatalog] = useState({ addons: [], prices: [] });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null); // {type, text}

  const [tab, setTab] = useState('plans');
  const [tier, setTier] = useState('growth');

  // Per-entity form drafts. A key is present only once it has been typed in,
  // which is what separates "changed" from "same as stored".
  const [limitForm, setLimitForm] = useState({}); // tier -> {limit key: string}
  const [priceForm, setPriceForm] = useState({}); // "tier:currency" -> {monthly, discount, offer}
  const [addonPriceForm, setAddonPriceForm] = useState({}); // "addon:currency" -> same shape
  const [link, setLink] = useState({
    tenant_id: '', tier: '', period: 'monthly', customer_email: '', url: '', sent_tier: '',
  });
  // GST registration. Not a pricing lever: below the turnover threshold
  // charging it would be collecting tax we cannot remit, and above it
  // registration is compulsory.
  const [taxSettings, setTaxSettings] = useState(null);
  const [taxForm, setTaxForm] = useState({});
  const [savingTax, setSavingTax] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const [review, setReview] = useState(null); // {title, note, changes, onApply}
  const [ackRisk, setAckRisk] = useState(false);
  const [applying, setApplying] = useState(false);

  const flash = useCallback((type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 6000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plans, pr, addons, pay, taxes] = await Promise.all([
        billingAPI.getPlans(), billingAPI.getPrices(), billingAPI.getAddons(),
        billingAPI.getPayments(), billingAPI.getTaxSettings(),
      ]);
      setPlanCatalog(plans.data || plans);
      setPrices((pr.data || pr).prices || []);
      setAddonCatalog(addons.data || addons);
      setPayments((pay.data || pay).payments || []);
      setTaxSettings(taxes.data || taxes);
      setTaxForm({});
    } catch (e) {
      flash('error', 'Failed to load billing config.');
    } finally {
      setLoading(false);
    }
  }, [flash]);
  useEffect(() => { load(); }, [load]);

  const limitFields = planCatalog.limit_fields || [];
  const planFor = (t) => (planCatalog.plans || []).find((plan) => plan.tier === t) || null;
  const priceKey = (entity, currency) => `${entity}:${currency}`;
  const priceRow = (t, currency) => prices.find((p) => p.tier === t && p.currency === currency);
  const addonPriceRow = (addon, currency) => (addonCatalog.prices || [])
    .find((p) => p.addon === addon && p.currency === currency);

  // A number input must receive a machine-readable value. Passing `4,999` to
  // type=number renders it blank, which made saved prices look missing even
  // when the API had returned them.
  const majorInput = (minor) => (minor == null ? '' : String(minor / 100));
  const toMinor = (major) => {
    const amount = Number(major);
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : null;
  };
  const toDiscount = (value) => {
    const discount = Number(value);
    return value !== '' && value != null && Number.isFinite(discount) && discount >= 0 && discount < 100
      ? discount
      : null;
  };
  const asNumber = (value) => (value == null || value === '' ? null : Number(value));
  // Whole currency units, exactly as the server stores it: a preview in paise
  // would show an annual price no customer is ever charged.
  const annualFromDiscount = (monthlyMinor, discount) => (
    monthlyMinor != null && discount != null
      ? Math.round((monthlyMinor * 12 * (1 - discount / 100)) / 100) * 100
      : null
  );
  const formattedAmount = (minor) => (minor == null ? '—' : (minor / 100).toLocaleString());
  const showMoney = (minor, currency) => (
    minor == null ? 'not set' : `${currency} ${formattedAmount(minor)}`
  );

  const largeMove = (from, to) => {
    if (from == null || to == null || from <= 0) return null;
    const percent = ((to - from) / from) * 100;
    if (Math.abs(percent) < LARGE_MOVE_PERCENT) return null;
    return `${percent > 0 ? '+' : ''}${Math.round(percent)}% change`;
  };

  // ---- what has actually changed ------------------------------------------
  const limitChanges = (t) => {
    const plan = planFor(t);
    const draft = limitForm[t] || {};
    const out = [];
    for (const field of limitFields) {
      if (!(field.key in draft)) continue;
      const stored = plan?.limits?.[field.key] ?? null;
      const raw = draft[field.key];
      const parsed = raw === '' ? NaN : Number(raw);
      const minimum = Number.isInteger(field.minimum) ? field.minimum : 0;
      const error = !Number.isInteger(parsed) || parsed < minimum
        ? `must be a whole number of ${minimum} or more`
        : null;
      if (!error && parsed === stored) continue;
      out.push({
        scope: 'Capacity',
        label: field.label,
        from: stored,
        to: error ? raw : parsed,
        format: error ? 'text' : 'int',
        error,
        risk: !error && stored != null && parsed < stored
          ? 'Cuts capacity for every current subscriber'
          : null,
      });
    }
    return out;
  };

  // Tier prices and add-on prices are the same document, so one comparison
  // serves both. A second copy is how the two tables would drift apart.
  const moneyChanges = (draft, row, currency, scope) => {
    if (!draft) return [];
    const out = [];
    if ('monthly' in draft) {
      const from = row?.monthly_amount_minor ?? null;
      const to = toMinor(draft.monthly);
      if (to !== from) {
        out.push({
          scope,
          label: 'Monthly charge',
          from,
          to,
          format: 'money',
          currency,
          error: to == null ? 'must be an amount above zero' : null,
          risk: largeMove(from, to),
        });
      }
    }
    if ('discount' in draft) {
      const from = asNumber(row?.annual_discount_percent);
      const to = toDiscount(draft.discount);
      if (to !== from) {
        out.push({
          scope,
          label: 'Annual discount',
          from,
          to,
          format: 'percent',
          error: draft.discount !== '' && to == null
            ? 'must be between 0 and 99.999999'
            : null,
          risk: null,
        });
      }
    }
    if ('offer' in draft) {
      const from = (row?.offer_label ?? '').trim();
      const to = (draft.offer ?? '').trim();
      if (to !== from) {
        out.push({ scope, label: 'Offer label', from, to, format: 'text', error: null, risk: null });
      }
    }
    return out;
  };

  const priceChanges = (t, currency) => moneyChanges(
    priceForm[priceKey(t, currency)], priceRow(t, currency), currency, `${currency} price`,
  );
  const addonChanges = (addon, currency) => moneyChanges(
    addonPriceForm[priceKey(addon, currency)], addonPriceRow(addon, currency),
    currency, `${currency} price`,
  );

  const tierChanges = (t) => [
    ...limitChanges(t),
    ...CURRENCIES.flatMap((currency) => priceChanges(t, currency)),
  ];
  const addonAllChanges = (addon) => CURRENCIES
    .flatMap((currency) => addonChanges(addon, currency));

  const planUnsaved = TIERS.reduce((n, t) => n + tierChanges(t).length, 0);
  const addonUnsaved = (addonCatalog.addons || [])
    .reduce((n, addon) => n + addonAllChanges(addon.key).length, 0);
  const totalUnsaved = planUnsaved + addonUnsaved;

  // Drafts live in component state, so a full page navigation is the one way
  // to lose them silently. In-app routing keeps them; the tab badges say so.
  useEffect(() => {
    if (totalUnsaved === 0) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [totalUnsaved]);

  const showValue = (change, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (change.format === 'money') return `${change.currency} ${(value / 100).toLocaleString()}`;
    if (change.format === 'percent') return `${value}%`;
    if (change.format === 'int') return Number(value).toLocaleString();
    return String(value);
  };

  const discardTier = (t) => {
    setLimitForm((form) => { const next = { ...form }; delete next[t]; return next; });
    setPriceForm((form) => {
      const next = { ...form };
      CURRENCIES.forEach((currency) => delete next[priceKey(t, currency)]);
      return next;
    });
  };
  const discardAddon = (addon) => setAddonPriceForm((form) => {
    const next = { ...form };
    CURRENCIES.forEach((currency) => delete next[priceKey(addon, currency)]);
    return next;
  });

  const report = (results) => {
    if (results.length === 0) return;
    const failed = results.filter((r) => !r.ok);
    const saved = results.filter((r) => r.ok).map((r) => r.what);
    if (failed.length === 0) {
      flash('success', `Saved ${saved.join(' and ')}. Existing subscribers update immediately unless they have a documented exception.`);
    } else {
      flash('error', `${failed.map((r) => `${r.what}: ${r.detail}`).join(' · ')}`
        + (saved.length ? ` (${saved.join(' and ')} saved)` : ''));
    }
  };

  // The API replaces a whole price row, so an unedited field is resent as the
  // value currently stored rather than dropped.
  const savePriceRow = (call, entity, currency, draft, row) => call(entity, {
    currency,
    monthly_amount_minor: toMinor(draft.monthly ?? majorInput(row?.monthly_amount_minor)),
    annual_discount_percent: toDiscount(
      draft.discount ?? (asNumber(row?.annual_discount_percent) ?? ''),
    ),
    offer_label: ((draft.offer ?? row?.offer_label ?? '').trim()) || null,
    is_active: true,
  });

  const applyTier = async (t) => {
    const results = [];
    if (limitChanges(t).length) {
      const plan = planFor(t);
      const draft = limitForm[t] || {};
      // The API replaces the whole capacity document, so every field is sent.
      // A field that is neither drafted nor stored would go as NaN and come
      // back as an opaque 400, so it is named here instead.
      const limits = {};
      const unusable = [];
      for (const field of limitFields) {
        const value = Number(draft[field.key] ?? plan?.limits?.[field.key]);
        if (!Number.isInteger(value)) unusable.push(field.label);
        limits[field.key] = value;
      }
      if (unusable.length) {
        report([{ ok: false, what: 'capacity', detail: `no value for ${unusable.join(', ')}` }]);
        return;
      }
      try {
        await billingAPI.updatePlan(t, { limits });
        results.push({ ok: true, what: 'capacity' });
        setLimitForm((form) => { const next = { ...form }; delete next[t]; return next; });
      } catch (e) {
        results.push({ ok: false, what: 'capacity', detail: errorText(e) });
      }
    }
    for (const currency of CURRENCIES) {
      if (!priceChanges(t, currency).length) continue;
      const key = priceKey(t, currency);
      try {
        await savePriceRow(
          billingAPI.upsertPrice, t, currency, priceForm[key] || {}, priceRow(t, currency),
        );
        results.push({ ok: true, what: `${currency} price` });
        setPriceForm((form) => { const next = { ...form }; delete next[key]; return next; });
      } catch (e) {
        results.push({ ok: false, what: `${currency} price`, detail: errorText(e) });
      }
    }
    report(results);
    await load();
  };

  const applyAddon = async (addon) => {
    const results = [];
    for (const currency of CURRENCIES) {
      if (!addonChanges(addon, currency).length) continue;
      const key = priceKey(addon, currency);
      try {
        await savePriceRow(
          billingAPI.upsertAddon, addon, currency,
          addonPriceForm[key] || {}, addonPriceRow(addon, currency),
        );
        results.push({ ok: true, what: `${currency} price` });
        setAddonPriceForm((form) => { const next = { ...form }; delete next[key]; return next; });
      } catch (e) {
        results.push({ ok: false, what: `${currency} price`, detail: errorText(e) });
      }
    }
    report(results);
    await load();
  };

  const openReview = (title, note, changes, onApply) => {
    setAckRisk(false);
    setReview({ title, note, changes, onApply });
  };

  const runReview = async () => {
    const job = review?.onApply;
    if (!job) return;
    setApplying(true);
    try {
      await job();
      setReview(null);
    } finally {
      setApplying(false);
    }
  };

  const saveTax = async () => {
    const registered = taxForm.is_registered ?? taxSettings?.registered ?? false;
    setSavingTax(true);
    try {
      await billingAPI.updateTaxSettings({
        is_registered: registered,
        rate_percent: Number(
          taxForm.rate_percent ?? taxSettings?.rate_percent ?? taxSettings?.statutory_rate_percent,
        ),
        gstin: (taxForm.gstin ?? taxSettings?.gstin ?? '').trim() || null,
      });
      flash('success', registered
        ? 'GST is now added at checkout.'
        : 'GST is off. Prices are charged as shown.');
      await load();
    } catch (e) {
      flash('error', `Save failed: ${errorText(e)}`);
    } finally {
      setSavingTax(false);
    }
  };

  const runReconcile = async () => {
    setReconciling(true);
    setReconcileResult(null);
    try {
      const { data } = await billingAPI.reconcilePayments();
      setReconcileResult(data);
      const applied = (data?.applied || []).length;
      flash(
        applied ? 'success' : 'info',
        applied
          ? `Applied ${applied} payment${applied === 1 ? '' : 's'} the webhook never confirmed.`
          : `Checked ${data?.checked ?? 0}; nothing was owed.`,
      );
      if (applied) await load();
    } catch (e) {
      flash('error', `Reconcile failed: ${errorText(e)}`);
    } finally {
      setReconciling(false);
    }
  };

  const genLink = async () => {
    try {
      const resp = await billingAPI.createPaymentLink({
        tenant_id: link.tenant_id.trim(),
        tier: link.tier,
        period: link.period,
        customer_email: link.customer_email || undefined,
      });
      const data = resp.data || resp;
      setLink((l) => ({ ...l, url: data.payment_link, sent_tier: `${l.tier} · ${l.period}` }));
      flash('success', 'Payment link created.');
    } catch (e) { flash('error', errorText(e)); }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading billing configuration…</div>;

  // ---- price editor, shared by tiers and add-ons ---------------------------
  const priceEditor = (currency, draft, row, onChange) => {
    const monthlyValue = draft.monthly ?? majorInput(row?.monthly_amount_minor);
    const discountValue = draft.discount ?? (asNumber(row?.annual_discount_percent) ?? '');
    const offerValue = draft.offer ?? (row?.offer_label ?? '');
    const annual = annualFromDiscount(toMinor(monthlyValue), toDiscount(discountValue));
    const changedMonthly = 'monthly' in draft
      && toMinor(draft.monthly) !== (row?.monthly_amount_minor ?? null);
    const changedDiscount = 'discount' in draft
      && toDiscount(draft.discount) !== asNumber(row?.annual_discount_percent);
    const changedOffer = 'offer' in draft
      && (draft.offer ?? '').trim() !== (row?.offer_label ?? '').trim();
    return (
      <div
        key={currency}
        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{currency}</span>
          {row?.monthly_amount_minor == null && (
            <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:text-amber-300">
              Not on sale in {currency}
            </span>
          )}
        </div>
        <Field
          label="Monthly charge"
          was={changedMonthly ? showMoney(row?.monthly_amount_minor, currency) : null}
        >
          <input
            type="number" min="0.01" step="0.01" value={monthlyValue}
            onChange={(e) => onChange('monthly', e.target.value)}
            className={inputClass(changedMonthly)}
            aria-label={`${currency} monthly charge`}
          />
        </Field>
        <Field
          label="Annual discount (%)"
          was={changedDiscount ? `${asNumber(row?.annual_discount_percent) ?? '—'}%` : null}
          hint="Keep odd values: they exactly reproduce the published annual price."
        >
          <input
            type="number" min="0" max="99.999999" step="0.000001" value={discountValue}
            onChange={(e) => onChange('discount', e.target.value)}
            className={inputClass(changedDiscount)}
            aria-label={`${currency} annual discount`}
          />
        </Field>
        <div className="rounded bg-gray-50 dark:bg-gray-900/60 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Charged upfront each year
          </div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {annual == null ? '—' : `${currency} ${formattedAmount(annual)}`}
          </div>
        </div>
        <Field
          label="Annual offer label"
          was={changedOffer ? ((row?.offer_label ?? '').trim() || '—') : null}
        >
          <input
            type="text" maxLength="160" value={offerValue}
            onChange={(e) => onChange('offer', e.target.value)}
            className={inputClass(changedOffer)}
            placeholder="Optional, shown on the annual plan only"
            aria-label={`${currency} annual offer label`}
          />
        </Field>
      </div>
    );
  };

  const plan = planFor(tier);
  const planLabel = plan?.label || tier;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing &amp; Payments</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          The catalogue every tenant is billed against. Saving applies to current subscribers
          immediately — a payment only ever flips a tenant to
          <span className="font-medium"> active paid</span>, and the manual “mark paid” action
          remains as a fallback.
        </p>
      </div>

      {msg && (
        <div className={`px-4 py-2 rounded text-sm ${msg.type === 'error'
          ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          : 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'}`}>
          {msg.text}
        </div>
      )}

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((entry) => {
          const pending = entry.id === 'plans'
            ? planUnsaved
            : entry.id === 'addons' ? addonUnsaved : 0;
          return (
            <button
              key={entry.id} type="button" onClick={() => setTab(entry.id)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium ${
                tab === entry.id
                  ? 'border-indigo-600 text-indigo-700 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {entry.label}
              {pending > 0 && (
                <span className="rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
                  {pending}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'plans' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Editing plan</span>
            {TIERS.map((entry) => {
              const pending = tierChanges(entry).length;
              return (
                <button
                  key={entry} type="button" onClick={() => setTier(entry)}
                  className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium capitalize ${
                    tier === entry
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {planFor(entry)?.label || entry}
                  {pending > 0 && (
                    <span className={`rounded-full px-1.5 text-[11px] font-semibold ${
                      tier === entry ? 'bg-white text-indigo-700' : 'bg-amber-500 text-white'
                    }`}>
                      {pending}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              What {planLabel} includes
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Capacity is shared by all currencies for this tier. A value of 0 disables that
              capacity, and “Team seats beyond the admin” counts the owner separately.
            </p>
            <div className="mt-4 grid gap-5 sm:grid-cols-2">
              {groupLimitFields(limitFields).map((group) => (
                <fieldset
                  key={group.title}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {group.title}
                  </legend>
                  <div className="space-y-3">
                    {group.fields.map((field) => {
                      const draft = limitForm[tier] || {};
                      const stored = plan?.limits?.[field.key] ?? null;
                      const value = draft[field.key] ?? (stored ?? '');
                      const changed = field.key in draft && Number(draft[field.key]) !== stored;
                      return (
                        <Field
                          key={field.key} label={field.label}
                          was={changed ? (stored == null ? '—' : stored.toLocaleString()) : null}
                        >
                          <input
                            type="number" min={field.minimum ?? 0} step="1" value={value}
                            onChange={(e) => setLimitForm((form) => ({
                              ...form,
                              [tier]: { ...form[tier], [field.key]: e.target.value },
                            }))}
                            className={inputClass(changed)}
                            aria-label={`${planLabel} ${field.label}`}
                          />
                        </Field>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              What {planLabel} costs
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Set the monthly charge and annual discount; Serviti calculates the full upfront
              annual charge.{' '}{taxSettings?.registered ? `Prices exclude ${taxSettings.rate_percent}% GST, which is added at checkout.` : 'GST is switched off, so these are the amounts charged.'}
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {CURRENCIES.map((currency) => priceEditor(
                currency,
                priceForm[priceKey(tier, currency)] || {},
                priceRow(tier, currency),
                (name, value) => setPriceForm((form) => ({
                  ...form,
                  [priceKey(tier, currency)]: {
                    ...form[priceKey(tier, currency)], [name]: value,
                  },
                })),
              ))}
            </div>
            <div className="mt-4">
              <SaveBar
                changes={tierChanges(tier)}
                onDiscard={() => discardTier(tier)}
                onReview={() => openReview(
                  `${planLabel} plan`,
                  'Capacity and price changes apply to every current subscriber on this plan as soon as they are saved.',
                  tierChanges(tier),
                  () => applyTier(tier),
                )}
              />
            </div>
          </Card>
        </div>
      )}

      {tab === 'addons' && (
        <div className="space-y-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Per-unit prices for the extras shown on every plan card. An add-on with no price in a
            currency cannot be bought by tenants billed in it.
          </p>
          {(addonCatalog.addons || []).map((addon) => (
            <Card key={addon.key}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{addon.label}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{addon.description}</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {CURRENCIES.map((currency) => priceEditor(
                  currency,
                  addonPriceForm[priceKey(addon.key, currency)] || {},
                  addonPriceRow(addon.key, currency),
                  (name, value) => setAddonPriceForm((form) => ({
                    ...form,
                    [priceKey(addon.key, currency)]: {
                      ...form[priceKey(addon.key, currency)], [name]: value,
                    },
                  })),
                ))}
              </div>
              <div className="mt-4">
                <SaveBar
                  changes={addonAllChanges(addon.key)}
                  onDiscard={() => discardAddon(addon.key)}
                  onReview={() => openReview(
                    addon.label,
                    'This price is charged per unit, for every tenant who buys this add-on from now on.',
                    addonAllChanges(addon.key),
                    () => applyAddon(addon.key),
                  )}
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'payments' && (
        <div className="space-y-5">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Generate a payment link
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              For invoicing a tenant on their behalf. The tenant is identified only by id, so the
              amount is shown below before the link is created — check it against the plan you
              agreed. Serviti resolves which currency to charge from the tenant’s own billing
              anchor, not from this form.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field
                label="Tenant ID"
                hint={link.tenant_id && !tenantIdLooksValid(link.tenant_id)
                  ? 'Not a valid tenant id — expected a UUID.'
                  : 'Copy from Tenant Management.'}
              >
                <input
                  value={link.tenant_id}
                  onChange={(e) => setLink((l) => ({ ...l, tenant_id: e.target.value, url: '' }))}
                  className={inputClass(false)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </Field>
              <Field label="Customer email" hint="Optional. The gateway sends the link here too.">
                <input
                  value={link.customer_email}
                  onChange={(e) => setLink((l) => ({ ...l, customer_email: e.target.value }))}
                  className={inputClass(false)} placeholder="optional"
                />
              </Field>
              <Field label="Plan">
                <select
                  value={link.tier}
                  onChange={(e) => setLink((l) => ({ ...l, tier: e.target.value, url: '' }))}
                  className={inputClass(false)}
                >
                  <option value="">Choose a plan…</option>
                  {TIERS.map((t) => (
                    <option key={t} value={t}>{planFor(t)?.label || t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Billing period">
                <select
                  value={link.period}
                  onChange={(e) => setLink((l) => ({ ...l, period: e.target.value, url: '' }))}
                  className={inputClass(false)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </Field>
            </div>

            {link.tier && (
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-4">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  This link will charge, before GST
                </div>
                <div className="mt-1 flex flex-wrap gap-6">
                  {CURRENCIES.map((currency) => {
                    const row = priceRow(link.tier, currency);
                    const minor = link.period === 'annual'
                      ? annualFromDiscount(
                        row?.monthly_amount_minor, asNumber(row?.annual_discount_percent),
                      )
                      : (row?.monthly_amount_minor ?? null);
                    return (
                      <span
                        key={currency}
                        className="text-sm font-semibold text-gray-900 dark:text-white"
                      >
                        {showMoney(minor, currency)}
                        <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                          / {link.period === 'annual' ? 'year' : 'month'}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4">
              <button
                type="button" onClick={genLink}
                disabled={!link.tier || !tenantIdLooksValid(link.tenant_id)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                Create link
              </button>
            </div>
            {link.url && (
              <p className="mt-3 text-sm text-gray-700 dark:text-gray-200">
                <span className="font-medium capitalize">{link.sent_tier}</span> link:{' '}
                <a
                  href={link.url} target="_blank" rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 underline break-all"
                >
                  {link.url}
                </a>
              </p>
            )}
          </Card>

          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Unconfirmed payments
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  A gateway webhook that never arrives leaves a customer charged
                  and without their plan, and nothing else reports it. This asks
                  the gateway directly about every payment we opened and never
                  applied, and grants what it confirms.
                </p>
              </div>
              <button
                type="button"
                onClick={runReconcile}
                disabled={reconciling}
                className="shrink-0 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {reconciling ? 'Checking…' : 'Reconcile overdue payments'}
              </button>
            </div>
            {reconcileResult && (
              <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-3 text-sm">
                <div className="text-gray-700 dark:text-gray-200">
                  Checked {reconcileResult.checked} · applied{' '}
                  {(reconcileResult.applied || []).length} · still unpaid{' '}
                  {reconcileResult.still_unpaid} · failed{' '}
                  {(reconcileResult.failed || []).length}
                </div>
                {(reconcileResult.applied || []).map((row) => (
                  <div key={row.id} className="mt-1 text-xs text-green-700 dark:text-green-400">
                    applied {row.id} → tenant {row.tenant_id}
                  </div>
                ))}
                {(reconcileResult.failed || []).map((row) => (
                  <div key={row.id} className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {row.id}: {row.reason}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent payments</h2>
            {payments.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No payments recorded yet.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Tenant</th>
                      <th className="text-left px-3 py-2">Provider</th>
                      <th className="text-left px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Applied</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800 dark:text-gray-100">
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                          {p.created_at?.slice(0, 16).replace('T', ' ')}
                        </td>
                        <td className="px-3 py-2 truncate max-w-[10rem]">{p.tenant_id || '—'}</td>
                        <td className="px-3 py-2">{p.provider}</td>
                        <td className="px-3 py-2">
                          {p.amount_minor != null
                            ? `${(p.amount_minor / 100).toLocaleString()} ${p.currency || ''}`
                            : '—'}
                        </td>
                        <td className="px-3 py-2">{p.status}</td>
                        <td className="px-3 py-2">{p.applied ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'tax' && taxSettings && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            GST registration
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Registration is compulsory once turnover crosses the threshold, and
            charging GST before registering means collecting tax that cannot be
            remitted. This switch decides both what customers are charged and
            whether checkout asks them for a place of supply.
          </p>

          <label className="mt-4 flex items-start gap-3">
            <input
              type="checkbox"
              checked={taxForm.is_registered ?? taxSettings.registered}
              onChange={(e) => setTaxForm((f) => ({ ...f, is_registered: e.target.checked }))}
              className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                Serviti is registered for GST
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                While this is off, every price is charged exactly as published and
                no tax invoice is promised.
              </span>
            </span>
          </label>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field
              label="Rate (%)"
              hint={`Statutory rate for software services is ${taxSettings.statutory_rate_percent}%.`}
            >
              <input
                type="number" min="0" max="99.999999" step="0.000001"
                value={taxForm.rate_percent ?? (taxSettings.rate_percent ?? '')}
                onChange={(e) => setTaxForm((f) => ({ ...f, rate_percent: e.target.value }))}
                className={inputClass('rate_percent' in taxForm)}
              />
            </Field>
            <Field
              label="Serviti's GSTIN"
              hint="Required before GST can be switched on: an invoice without the supplier's GSTIN is not a tax invoice."
            >
              <input
                type="text" maxLength="15"
                value={taxForm.gstin ?? (taxSettings.gstin ?? '')}
                onChange={(e) => setTaxForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                className={inputClass('gstin' in taxForm)}
                placeholder="22AAAAA0000A1Z5"
              />
            </Field>
          </div>

          <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-900/60 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
            Changing this affects future checkouts only. Every payment already
            taken keeps the tax it was charged, because each one stores its own
            figures.
          </div>

          <button
            type="button"
            onClick={saveTax}
            disabled={savingTax || Object.keys(taxForm).length === 0}
            className="mt-4 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {savingTax ? 'Saving…' : 'Save tax settings'}
          </button>
        </Card>
      )}

      {review && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-gray-800 p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Review {review.title}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{review.note}</p>
            <div className="mt-4 max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="text-left px-3 py-2">Field</th>
                    <th className="text-left px-3 py-2">Now</th>
                    <th className="text-left px-3 py-2">After saving</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800 dark:text-gray-100">
                  {review.changes.map((change, index) => (
                    <tr
                      key={`${change.scope}:${change.label}:${index}`}
                      className={`border-t border-gray-200 dark:border-gray-700 ${
                        change.error || change.risk ? 'bg-amber-50 dark:bg-amber-950/30' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{change.label}</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {change.scope}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {showValue(change, change.from)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{showValue(change, change.to)}</div>
                        {change.error && (
                          <div className="text-[11px] font-medium text-red-600 dark:text-red-400">
                            {change.error}
                          </div>
                        )}
                        {!change.error && change.risk && (
                          <div className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                            {change.risk}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {review.changes.some((c) => c.error) ? (
              <p className="mt-3 text-sm font-medium text-red-600 dark:text-red-400">
                Fix the highlighted values before saving.
              </p>
            ) : review.changes.some((c) => c.risk) && (
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox" checked={ackRisk}
                  onChange={(e) => setAckRisk(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300"
                />
                I have checked the highlighted changes.
              </label>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button" onClick={() => setReview(null)} disabled={applying}
                className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button" onClick={runReview}
                disabled={
                  applying
                  || review.changes.some((c) => c.error)
                  || (review.changes.some((c) => c.risk) && !ackRisk)
                }
                className="rounded bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {applying ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingConfig;
